import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { Children, cloneElement, isValidElement, useRef, } from 'react';
import { Loading, Toolbar, useResourceContext } from 'react-admin';
import { Card, CardContent, MenuList } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useScrollSpy } from './useScrollSpy';
import { LongFormSectionMenuItem } from './LongFormSectionMenuItem';
import { useAuthorizedChildren, } from '../useAuthorizedChildren';
/**
 * Form layout for long forms.
 *
 * Renders a fixed table of contents and toolbar, as well as section headers.
 * Expects `<LongForm.Section>` as children, each having a label.
 *
 * @example
 * import { LongForm } from '@react-admin/ra-form-layout';
 *
 * const CustomerEdit = () => (
 *     <Edit component="div">
 *         <LongForm>
 *             <LongForm.Section label="Identity">
 *                 <Labeled label="id">
 *                     <TextField source="id" />
 *                 </Labeled>
 *                 <TextInput source="first_name" validate={required()} />
 *                 <TextInput source="last_name" validate={required()} />
 *                 <DateInput source="dob" label="born" validate={required()} />
 *                 <SelectInput source="sex" choices={sexChoices} />
 *             </LongForm.Section>
 *             <LongForm.Section label="Occupations">
 *                 <ArrayInput source="occupations" label="">
 *                     <SimpleFormIterator>
 *                         <TextInput source="name" validate={required()} />
 *                         <DateInput source="from" validate={required()} />
 *                         <DateInput source="to" />
 *                     </SimpleFormIterator>
 *                 </ArrayInput>
 *             </LongForm.Section>
 *             <LongForm.Section label="Preferences">
 *                 <SelectInput
 *                     source="language"
 *                     choices={languageChoices}
 *                     defaultValue="en"
 *                 />
 *                 <BooleanInput source="dark_theme" />
 *                 <BooleanInput source="accepts_emails_from_partners" />
 *             </LongForm.Section>
 *         </LongForm>
 *     </Edit>
 * );
 */
export var LongFormView = function (_a) {
    var _b = _a.authorizationError, authorizationError = _b === void 0 ? null : _b, children = _a.children, enableAccessControl = _a.enableAccessControl, _c = _a.loading, loading = _c === void 0 ? DefaultLoading : _c, sx = _a.sx, toolbar = _a.toolbar;
    var _d = useAuthorizedChildren({
        children: children,
        action: 'write',
        getResourceKey: getResourceKey,
        filterChildren: filterChildren,
        enabled: enableAccessControl,
    }), authorizedChildren = _d.authorizedChildren, isPending = _d.isPending, error = _d.error;
    // section refs allow to build the table of contents automatically
    var nbSections = Children.count(authorizedChildren);
    var sectionRefs = useRef(new Array(nbSections));
    var sectionsSignature = useRef(undefined);
    var resource = useResourceContext();
    React.useEffect(function () {
        var _a;
        if (!enableAccessControl)
            return;
        if (!authorizedChildren)
            return;
        // We only want to reset the section refs when the children actually change
        // To ensure that, we compute a signature of the children and compare it to the previous one
        var newSectionSignature = (_a = Children.map(authorizedChildren, function (child) {
            return isValidElement(child) ? getResourceKey(child, resource !== null && resource !== void 0 ? resource : '') : '';
        })) === null || _a === void 0 ? void 0 : _a.join();
        if (sectionsSignature.current &&
            sectionsSignature.current === newSectionSignature)
            return;
        sectionsSignature.current = newSectionSignature;
        var newNbSections = Children.count(authorizedChildren);
        // To avoid losing already set refs, we only add new ones if necessary
        if (newNbSections > sectionRefs.current.length) {
            sectionRefs.current.push(undefined);
        }
    }, [authorizedChildren, enableAccessControl, resource]);
    // track the scroll to highlight the current section
    var activeSection = useScrollSpy({
        sectionElements: sectionRefs.current,
        offsetPx: -80,
    });
    if (error) {
        return authorizationError;
    }
    if (enableAccessControl && isPending) {
        return loading;
    }
    return (_jsxs(Root, { sx: sx, className: LongFormViewClasses.root, children: [_jsx(Card, { className: LongFormViewClasses.toc, children: _jsx(MenuList, { children: Children.map(authorizedChildren, function (formSection, index) {
                        return isValidElement(formSection) ? (_jsx(LongFormSectionMenuItem, { selected: activeSection === index, name: formSection.props.label, index: index, cardinality: formSection.props.cardinality, sectionRefs: sectionRefs }, index)) : null;
                    }) }) }), _jsxs(Card, { className: LongFormViewClasses.main, children: [_jsx(CardContent, { children: Children.map(authorizedChildren, function (formSection, index) {
                            var _a;
                            return isValidElement(formSection)
                                ? cloneElement(formSection, {
                                    ref: function (ref) {
                                        if (ref == null) {
                                            return;
                                        }
                                        sectionRefs.current[index] = ref;
                                    },
                                    key: index,
                                    enableAccessControl: (_a = formSection.props
                                        .enableAccessControl) !== null && _a !== void 0 ? _a : enableAccessControl,
                                })
                                : null;
                        }) }), toolbar ? (cloneElement(toolbar, {
                        className: LongFormViewClasses.toolbar,
                    })) : (_jsx(Toolbar, { className: LongFormViewClasses.toolbar }))] })] }));
};
var DefaultLoading = _jsx(Loading, {});
var getResourceKey = function (child, resource) {
    var _a;
    return "".concat(resource, ".section.").concat((_a = child.props.id) !== null && _a !== void 0 ? _a : child.props.label);
};
var filterChildren = function (child) {
    if (child.props.id == null && child.props.label == null) {
        if (process.env.NODE_ENV === 'development') {
            console.warn("You enabled access control on the LongForm but didn't provide an id nor a label to its sections. Access control cannot be enforced on some sections.");
        }
        return false;
    }
    return true;
};
var PREFIX = 'RaLongForm';
export var LongFormViewClasses = {
    root: "".concat(PREFIX, "-root"),
    toc: "".concat(PREFIX, "-toc"),
    main: "".concat(PREFIX, "-main"),
    toolbar: "".concat(PREFIX, "-toolbar"),
    error: "".concat(PREFIX, "-error"),
};
var Root = styled('div', {
    name: PREFIX,
    overridesResolver: function (props, styles) { return styles.root; },
})(function (_a) {
    var _b;
    var theme = _a.theme;
    return (_b = {},
        _b["& .".concat(LongFormViewClasses.toc)] = { position: 'fixed', width: 200 },
        _b["& .".concat(LongFormViewClasses.main)] = {
            marginLeft: '220px',
            overflow: 'visible',
        },
        _b["& .".concat(LongFormViewClasses.toolbar)] = {
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
        },
        _b["& .".concat(LongFormViewClasses.error)] = {
            color: theme.palette.error.main,
        },
        _b);
});
